import styled, { keyframes } from 'styled-components';
import { SemiBold, Bold, Medium } from '@src/apps/styles/common-style';

const modalSpin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

export const ModalOverlayStyle = styled('div')`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgb(0 0 0 / 45%);
`;

// inline (non-fixed) loader wrapper so the spinner stays visible within the modal
// regardless of ancestor stacking/containing-block CSS that can trap `position: fixed` elements
export const ModalLoaderStyle = styled('div')`
  display: flex;
  flex: 1;
  width: 100%;
  min-height: 300px;
  align-items: center;
  justify-content: center;
`;

// spinner defined locally with explicit border so a global border reset can't zero it out
export const ModalSpinnerStyle = styled('div')`
  width: 50px;
  height: 50px;
  border-width: 5px !important;
  border-style: solid !important;
  border-color: rgba(0, 0, 0, 0.1) !important;
  border-top-color: #3498db !important;
  border-radius: 50%;
  animation: ${modalSpin} 1s linear infinite;
`;

export const ModalContentStyle = styled('div')`
  display: inline-flex;
  width: 887px;
  height: auto;
  max-height: calc(100vh - 48px);
  box-sizing: border-box;
  overflow: hidden;
  padding: 24px;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  border-radius: 2px;
  background: #f1f4f7;
  box-shadow: 0 20px 40px 0 rgba(0, 0, 0, 0.15);
`;

export const ModalHeaderStyle = styled('header')`
  display: flex;
  flex-shrink: 0;
  align-items: flex-start;
  justify-content: space-between;
  padding: 30px 30px 0px 30px;
  color: rgb(45, 45, 57) !important;
  font-family: ${Medium};
`;

export const ModalDescriptionStyle = styled('p')`
  margin-top: 16px;
  margin-bottom: 0;
  color: #757588;
  font-family: ${Medium};
  font-size: 16px;
  font-style: normal;
  line-height: normal;
`;

export const InvalidContractsErrorStyle = styled('div')`
  display: flex;
  flex-shrink: 0;
  margin: 0 !important;
  padding: 8px 8px 8px 12px !important;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  vertical-align: middle !important;
  box-sizing: border-box;
  border-radius: 4px;
  margin-right: 30px !important;
  margin-left: 30px !important;
  border-top: 1px solid #c40000 !important;
  border-right: 1px solid #c40000 !important;
  border-bottom: 1px solid #c40000 !important;
  border-left: 4px solid #c40000 !important;
  background: #f9e5e5;
  color: #323333;
  font-family: ${Bold};
  font-size: 16px !important;
  font-style: normal;
  line-height: 20px;

  svg {
    fill: #c40000;
  }
`;

export const InvalidContractsErrorIconStyle = styled('span')`
  display: flex;
  flex-shrink: 0;
  color: #c40000;
`;

export const InvalidContractsErrorTextStyle = styled('span')`
  flex: 1;
`;

export const InvalidContractsErrorCloseStyle = styled('button')`
  display: flex;
  flex-shrink: 0;
  padding: 0;
  color: #000000;
  background: transparent;
  border: 0;
  cursor: pointer;
`;

export const ModalTitleStyle = styled('div')`
  display: flex;
  width: 787px;
  height: 24px;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  margin: 0;
  color: #2d2d39 !important;
  font-family: ${SemiBold} !important;
  font-size: 22px !important;
  font-style: normal;
  line-height: normal;
  font-feature-settings: 'liga' off, 'clig' off;
`;

export const CloseButtonStyle = styled('button')`
  color: #000000;
  font-size: 1.5rem;
  background: transparent;
  border: 0;
  width: 12px;
  height: 12px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  & > div {
    display: flex;
  }

  & svg {
    width: 16px;
    height: 16px;
  }
`;

export const SearchSectionStyle = styled('section')`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  /* gap: 8px; */
  align-self: stretch;
  padding: 0px 0px 30px 30px;
  border-bottom: 1px solid #e0e0e0;
`;

export const SearchControlsRowStyle = styled('div')`
  display: flex;
  align-items: flex-end;
  gap: 40px;
  align-self: stretch;
`;

export const SearchFieldGroupStyle = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SearchLabelStyle = styled('label')`
  color: #4b4d4f;
  font-family: ${SemiBold};
  font-size: 16px;
  height: 24px;
  margin-top: 16px;
  font-style: normal;
  line-height: 140%;
`;

export const SearchInputWrapperStyle = styled('div')`
  position: relative;
  width: 276px;
`;

export const SearchIconWrapperStyle = styled('span')`
  position: absolute;
  top: 50%;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4b4d4f;
  transform: translateY(-50%);
  pointer-events: none;
`;

export const SearchInputStyle = styled('input')`
  display: flex;
  width: 276px;
  height: 40px;
  box-sizing: border-box;
  padding: 0 12px;
  align-items: center;
  gap: 12px;
  align-self: stretch;
  border-radius: 4px;
  border: 1px solid #4b4d4f !important;
  background: #fff !important;
  font-size: 16px;

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: #4b4d4f !important;
    box-shadow: none;
  }
`;

export const SearchButtonStyle = styled('button')`
  min-height: 40px;
  padding: 8px 16px;
  color: #fff;
  background: #005eb8;
  border: 1px solid #005eb8;
  cursor: pointer;

  &:disabled {
    color: #7b8491;
    background: #eef0f2;
    border-color: #c8cdd3;
    cursor: not-allowed;
  }
`;

export const ApplyButtonStyle = SearchButtonStyle;

export const CancelButtonStyle = styled('button')`
  min-height: 40px;
  padding: 8px 16px;
  color: #005eb8;
  background: #fff;
  border: 1px solid #005eb8;
  cursor: pointer;
`;

export const SecondaryButtonStyle = styled(CancelButtonStyle)`
  &:disabled {
    color: #7b8491;
    background: #eef0f2;
    border-color: #c8cdd3;
    cursor: not-allowed;
  }
`;

export const SearchResultsStyle = styled('section')`
  display: flex;
  flex: 0 1 auto;
  min-height: 0;
  width: 826px;
  flex-direction: column;
  overflow: hidden;
  padding: 8px 30px 30px 30px;
`;

export const ResultsHeaderStyle = styled('div')`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  height: 9px;
`;

export const ResultsCountLabelStyle = styled('span')`
  color: #4a5568;
  font-size: 14px;
`;

export const DeselectAllLinkStyle = styled('button')`
  padding: 0;
  color: #005eb8;
  font-size: 14px;
  text-decoration: none;
  background: transparent;
  border: 0;
  cursor: pointer;

  &:disabled {
    color: #7b8491;
    text-decoration: none;
    cursor: not-allowed;
  }
`;

export const ResultsListStyle = styled('ul')`
  display: flex;
  flex: 0 1 auto;
  flex-direction: column;
  min-height: 0;
  height: auto;
  max-height: none;
  overflow-y: auto;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const ResultsRowStyle = styled('li')`
  display: flex;
  box-sizing: border-box;
  // width: 828px;
  //min-width: 828px;
  height: 64px;
  padding: 16px !important;
  align-items: center;
  gap: 20px;
  border-bottom: 1px solid #e0e0e0;
  background: #fff;
`;

export const ResultItemStyle = styled('li')<{ isDisabled?: boolean }>`
  display: flex;
  flex: 0 1 calc(20% - 16px);
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  border-radius: 2px;
  background: #fff;

  ${(props) =>
    props.isDisabled &&
    `
    background: #f7f8fa;
    border-color: #e5e7eb;
    cursor: not-allowed;
  `}
`;

export const CheckboxStyle = styled('input')`
  width: 20px;
  height: 20px;
  accent-color: #196ecf;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

export const SelectAllRowStyle = styled('div')`
  display: flex;
  flex-shrink: 0;
  padding: 12px 16px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-bottom: 1px solid #e0e0e0 !important;
  background: #fff;
  height: 44px;
  margin-top: 8px !important;
  padding-left: 16px !important;
`;

export const SelectAllTextStyle = styled('span')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
`;

export const ContractContentStyle = styled('span')`
  display: flex;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;
`;

export const ContractIdTextStyle = styled('span')`
  overflow: hidden;
  color: #2d2d39;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${SemiBold};
  font-size: 16px;
  font-style: normal;
  line-height: normal;

  [aria-disabled='true'] & {
    color: #9aa0a6;
  }
`;

export const TaxIdCountTextStyle = styled('span')`
  overflow: hidden;
  color: #757588;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${Medium};
  font-size: 14px;
  font-style: normal;
  line-height: normal;

  [aria-disabled='true'] & {
    color: #9aa0a6;
  }
`;

export const SelectionSummaryStyle = styled('section')`
  flex: 1;
`;

export const SummaryRowGroupStyle = styled('div')`
  display: flex;
  align-items: flex-end;
  gap: 40px;
`;

export const SummaryRowStyle = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

export const SummaryLabelStyle = styled('span')`
  color: #757588;
  font-family: ${Medium};
  font-size: 16px;
  font-style: normal;
  line-height: normal;
`;

export const SummaryValueStyle = styled('span')`
  color: #2d2d39;
  font-family: ${SemiBold};
  font-size: 16px;
  font-style: normal;
  line-height: normal;
`;

export const SummaryDividerStyle = styled('span')`
  display: none;
`;

export const ModalFooterStyle = styled('footer')`
  display: flex;
  flex-shrink: 0;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  align-self: stretch;
  padding: 16px 30px;
  background: #fff;
  border-top: 1px solid #f0f0f0;
`;

export const PaginationWrapperStyle = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  flex: 0 0 448px;
  width: 448px;
  height: 48px;
  padding: 12px 16px;
  gap: 8px;
  box-sizing: border-box;

  > div,
  .rlp-table-footer {
    width: 100%;
  }

  .uhci-table-footer {
    display: flex !important;
    align-items: center !important;
    height: 40px !important;
    gap: 40px;
    padding: 0 !important;
    border-top: none !important;
  }

  .rlp-left-section,
  .rlp-right-section {
    display: flex !important;
    align-items: center !important;
    float: none !important;
  }

  .rlp-left-section {
    gap: 8px;
  }

  .rlp-right-section {
    gap: 8px;
  }

  .rlp-footer-label {
    color: #757588;
    font-family: 'UHC Sans';
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    white-space: nowrap;
  }

  .selectedItem > div {
    display: flex;
    width: 72px;
    height: 40px;
    align-items: center;
    padding: 8px 10px 8px 16px;
    box-sizing: border-box;
    border: 1px solid #b3babc !important;
    border-radius: 2px;
    background: #fff;
  }

  .selectedItem [class*='singleValue'] {
    color: #2d2d39;
    font-family: 'UHC Sans';
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
  }

  .selectedItem [class*='control'] {
    border-color: #b3babc !important;
    box-shadow: none !important;
    width: 40px;
  }

  .selectedItem [class*='control']:hover,
  .selectedItem [class*='control']:focus,
  .selectedItem [class*='control']:focus-within {
    outline: none;
  }

  .selectedItem [class*='indicatorSeparator'] {
    display: none;
  }

  .rlp-right-section form {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .rlp-right-section .page-number {
    display: flex;
    width: 49px !important;
    height: 40px;
    padding: 8px 16px;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    color: #2d2d39;
    border: 1px solid var(--border-2, #bac0c2);
    border-radius: 2px;
    text-align: center;
  }

  .pagination-btn-widget {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .pagination-btn-widget .next-button {
    margin-left: 0 !important;
  }

  .pagination-btn-widget button {
    display: flex;
    width: 40px !important;
    height: 40px;
    padding: 8px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border-radius: 2px;
  }
`;

export const ModalFooterActionsStyle = styled('div')`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 12px;
  margin-left: auto;
`;

export const FooterCancelButtonStyle = styled('button')`
  display: flex;
  height: 40px;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  color: var(--UHC-Insights-brand-blue, #196ecf);
  font-family: 'UHC Sans';
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  background: #fff;
  border: 1px solid var(--UHC-Insights-brand-blue, #196ecf);
  border-radius: 2px;
  cursor: pointer;
`;

export const FooterApplyButtonStyle = styled('button')`
  display: flex;
  height: 40px;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  color: #fff;
  font-family: 'UHC Sans';
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  background: #196ecf;
  border: 1px solid var(--UHC-Insights-brand-blue, #196ecf);
  border-radius: 2px;
  cursor: pointer;

  &:disabled {
    background: #cccccc;
    border-color: #cccccc;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const ErrorMessageStyle = styled('div')`
  padding: 12px;
  color: #9b1c1c;
  background: #fff3f3;
`;

export const LoadingSpinnerStyle = styled('div')`
  color: #4a5568;
`;
